import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  DollarSign, TrendingUp, TrendingDown, Users, CreditCard, AlertTriangle,
  RefreshCw, PieChart, BarChart2,
  Package, Clock, Target, Zap, Heart, CheckCircle, Percent, Wallet, ExternalLink, XCircle, Shield, CalendarDays,
  UserMinus, LineChart as LineChartIcon, Grid3X3, Server, Layers, Sparkles, Award
} from 'lucide-react'
import { BarChart, Bar, PieChart as RePie, Pie, Cell, Line, ComposedChart,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, AreaChart, LineChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ReferenceLine } from 'recharts'
import { adminApi } from '../services/ApiService'
import InventoryService from '../services/InventoryService'
import CashFlowSankey from '../components/CashFlowSankey'
import './FinanceHub.css'

const PLANS = {
  free:      { name: 'Sin plan', color: '#6b7280', icon: '🆓' },
  basico:    { name: 'Básico',    color: '#3b82f6', icon: '⚡' },
  pro:       { name: 'Pro',       color: '#8b5cf6', icon: '🚀' },
  comercial: { name: 'Comercial', color: '#f59e0b', icon: '🏢' },
}
const DEFAULT_PLAN_PRICES = { basico: 9990, pro: 14990, comercial: 44990 }
const ANNUAL_PLAN_PRICES = { basico: 99990, pro: 149990, comercial: 449990 }
const DEFAULT_DEVICE_ECONOMICS = {
  'GP-BOX':   { cost: 80000,  price: 389900 },
  'GP-MINI':  { cost: 18000,  price: 49990  },
  'GP-CAM':   { cost: 35000,  price: 129900 },
  'GP-HIDRO': { cost: 95000,  price: 459900 },
  'GP-RELAY': { cost: 110000, price: 499900 },
}
const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4']

function fmt(n) { return '$' + (n || 0).toLocaleString('es-AR') }

export default function FinanceHub() {
  const [tab, setTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [receipts, setReceipts] = useState([])
  const [recurring, setRecurring] = useState([])
  const [products, setProducts] = useState([])
  const [bomSummary, setBomSummary] = useState({})

  // Cash flow allocation config
  const [taxRate, setTaxRate] = useState(35)
  const [armado3dPct, setArmado3dPct] = useState(4)
  const [armadoElecPct, setArmadoElecPct] = useState(4)
  const [ventaPct, setVentaPct] = useState(13)
  const [reinvestPct, setReinvestPct] = useState(20)
  const [monthlyOpex, setMonthlyOpex] = useState(0)

  // Simulation overrides (null = use calculated value)
  const [simDevRev, setSimDevRev] = useState(null)
  const [simMRR, setSimMRR] = useState(null)
  const [simRepos, setSimRepos] = useState(null)

  // MercadoPago state
  const [mpAccount, setMpAccount] = useState(null)
  const [mpBalance, setMpBalance] = useState(null)
  const [mpSubscriptions, setMpSubscriptions] = useState(null)
  const [mpLoading, setMpLoading] = useState(false)
  const [mpError, setMpError] = useState(null)
  const [mpSubFilter, setMpSubFilter] = useState('')

  // Churn, Forecast, Cohort, Cost Allocation state
  const [churnData, setChurnData] = useState(null)
  const [churnLoading, setChurnLoading] = useState(false)
  const [forecastData, setForecastData] = useState(null)
  const [forecastLoading, setForecastLoading] = useState(false)
  const [cohortData, setCohortData] = useState(null)
  const [cohortLoading, setCohortLoading] = useState(false)
  const [costAllocData, setCostAllocData] = useState(null)
  const [costAllocLoading, setCostAllocLoading] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [cRes, iRes, rRes, recRes] = await Promise.all([
        adminApi.get('/admin/clients'),
        adminApi.get('/admin/invoices'),
        adminApi.get('/admin/payment-receipts'),
        adminApi.get('/admin/recurring-invoices'),
      ])
      setClients((cRes.clients || []).map(c => {
        const isTrial = c.isTrial === true
        let trialExpired = false, trialDaysRemaining = null
        if (isTrial && c.subscriptionExpiresAt) {
          const diffMs = new Date(c.subscriptionExpiresAt) - new Date()
          trialDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
          trialExpired = diffMs <= 0
        }
        const isActive = c.subscriptionActive !== false
        const hasExpiration = !!c.subscriptionExpiresAt
        const expirationPassed = hasExpiration && new Date(c.subscriptionExpiresAt) < new Date()
        const isExpired = !isActive && (expirationPassed || !!c.lastPaymentDate)
        return { ...c, subscriptionActive: isActive, plan: c.plan || 'free', isTrial, trialExpired, trialDaysRemaining, isWhitelisted: c.isWhitelisted === true, isExpired }
      }))
      setInvoices(iRes.invoices || iRes.documents || [])
      setReceipts(rRes.paymentReceipts || [])
      setRecurring(recRes.recurringInvoices || [])
    } catch (e) { console.error('FinanceHub load:', e) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    loadData()
    InventoryService.listProducts().then(res => setProducts(res.products || [])).catch(() => {})
    InventoryService.getDashboard().then(res => { if (res.success && res.bomSummary) setBomSummary(res.bomSummary) }).catch(() => {})
  }, [loadData])

  // Load MercadoPago data when tab is selected
  const loadMPData = useCallback(async () => {
    setMpLoading(true)
    setMpError(null)
    try {
      const [accRes, balRes, subsRes] = await Promise.all([
        adminApi.get('/admin/mercadopago/account'),
        adminApi.get('/admin/mercadopago/balance'),
        adminApi.get('/admin/mercadopago/subscriptions', mpSubFilter ? { status: mpSubFilter } : {}),
      ])
      setMpAccount(accRes)
      setMpBalance(balRes)
      setMpSubscriptions(subsRes)
    } catch (e) {
      console.error('MP load error:', e)
      setMpError(e.message)
    } finally {
      setMpLoading(false)
    }
  }, [mpSubFilter])

  useEffect(() => {
    if (tab === 'mercadopago' && !mpAccount && !mpLoading) loadMPData()
    if (tab === 'analytics') {
      if (!churnData && !churnLoading) {
        setChurnLoading(true)
        adminApi.get('/admin/analytics/churn').then(setChurnData).catch(() => {}).finally(() => setChurnLoading(false))
      }
      if (!forecastData && !forecastLoading) {
        setForecastLoading(true)
        adminApi.get('/admin/analytics/revenue-forecast').then(setForecastData).catch(() => {}).finally(() => setForecastLoading(false))
      }
      if (!cohortData && !cohortLoading) {
        setCohortLoading(true)
        adminApi.get('/admin/analytics/cohorts').then(setCohortData).catch(() => {}).finally(() => setCohortLoading(false))
      }
      if (!costAllocData && !costAllocLoading) {
        setCostAllocLoading(true)
        adminApi.get('/admin/analytics/cost-allocation').then(setCostAllocData).catch(() => {}).finally(() => setCostAllocLoading(false))
      }
    }
  }, [tab])

  // Reload when sub filter changes (only if already loaded)
  useEffect(() => {
    if (mpAccount) loadMPData()
  }, [mpSubFilter])

  // ── Dynamic pricing from products + BOM ──
  const { planPrices, deviceEconomics } = useMemo(() => {
    const pp = { ...DEFAULT_PLAN_PRICES }
    const de = { ...DEFAULT_DEVICE_ECONOMICS }
    for (const [model, parts] of Object.entries(bomSummary)) {
      const bomCost = (parts || []).reduce((s, p) => s + (p.lineCost || 0), 0)
      if (bomCost > 0) { if (!de[model]) de[model] = { cost: 0, price: 0 }; de[model] = { ...de[model], cost: bomCost } }
    }
    for (const p of products) {
      if (p.category === 'suscripcion' && p.sku) { const key = p.sku.toLowerCase(); if (pp[key] !== undefined) pp[key] = p.salePrice }
      else if (p.category === 'dispositivo' && p.sku) {
        const model = p.sku.toUpperCase(); if (!de[model]) de[model] = { cost: 0, price: 0 }
        if (p.salePrice) de[model] = { ...de[model], price: p.salePrice }
        if (p.costPrice && !bomSummary[model]) de[model] = { ...de[model], cost: p.costPrice }
      }
    }
    return { planPrices: pp, deviceEconomics: de }
  }, [products, bomSummary])

  // ── Per-Client Billing ──
  const getClientBilling = useCallback((client) => {
    const devRevenue = (client.devices || []).reduce((sum, d) => sum + (deviceEconomics[d.model]?.price || 0), 0)
    const devCost = (client.devices || []).reduce((sum, d) => sum + (deviceEconomics[d.model]?.cost || 0), 0)
    const isPaying = client.subscriptionActive && !client.isTrial && !client.isWhitelisted
    const isAnnual = client.billingCycle === 'annual'
    const monthlyPrice = planPrices[client.plan] || 0
    const planPrice = isAnnual ? (ANNUAL_PLAN_PRICES[client.plan] || monthlyPrice * 12) : monthlyPrice
    let subPeriods = 0
    if (isPaying && client.registrationDate) {
      const reg = new Date(client.registrationDate), now = new Date()
      const totalMonths = Math.max(1, (now.getFullYear() - reg.getFullYear()) * 12 + now.getMonth() - reg.getMonth())
      subPeriods = isAnnual ? Math.max(1, Math.ceil(totalMonths / 12)) : totalMonths
    }
    const subRevenue = isPaying ? subPeriods * planPrice : 0
    return { devRevenue, devCost, subRevenue, total: devRevenue + subRevenue }
  }, [planPrices, deviceEconomics])

  // ── MRR & Subscription Metrics ──
  const subMetrics = useMemo(() => {
    // Include clients with active sub OR with MP authorized (manual deactivation but still paying)
    const paying = clients
      .filter(c => {
        if (c.isTrial || c.isWhitelisted) return false
        return c.subscriptionActive || c.mercadopagoStatus === 'authorized'
      })
      .map(c => {
        // If deactivated but MP authorized, resolve effective plan from previousPlan
        if (!c.subscriptionActive && c.mercadopagoStatus === 'authorized') {
          const effectivePlan = c.previousPlan && c.previousPlan !== 'free' ? c.previousPlan : c.plan
          return { ...c, plan: effectivePlan !== 'free' ? effectivePlan : 'pro' }
        }
        return c
      })

    const mrr = paying.reduce((sum, c) => {
      const monthly = planPrices[c.plan] || planPrices.pro
      if (c.billingCycle === 'annual') {
        const annual = ANNUAL_PLAN_PRICES[c.plan] || monthly * 12
        return sum + Math.round(annual / 12)
      }
      return sum + monthly
    }, 0)
    const arr = mrr * 12
    const arpu = paying.length > 0 ? Math.round(mrr / paying.length) : 0

    const planBreakdown = Object.entries(planPrices).map(([key, price]) => {
      const planClients = paying.filter(c => c.plan === key)
      const annualCount = planClients.filter(c => c.billingCycle === 'annual').length
      const monthlyCount = planClients.length - annualCount
      const revenue = planClients.reduce((sum, c) => {
        if (c.billingCycle === 'annual') {
          return sum + Math.round((ANNUAL_PLAN_PRICES[key] || price * 12) / 12)
        }
        return sum + price
      }, 0)
      return { key, name: PLANS[key]?.name || key, count: planClients.length, annualCount, monthlyCount, revenue, color: PLANS[key]?.color || '#64748b', icon: PLANS[key]?.icon || '' }
    })

    const trials = clients.filter(c => c.isTrial && !c.trialExpired)
    const expired = clients.filter(c => c.trialExpired)
    const convRate = (paying.length + trials.length) > 0
      ? Math.round((paying.length / (paying.length + trials.length)) * 100) : 0

    return { mrr, arr, arpu, paying: paying.length, payingClients: paying, trials: trials.length, expired: expired.length, convRate, planBreakdown }
  }, [clients, planPrices])

  // ── Business Metrics (migrated from ClientsHub negocio) ──
  const bizMetrics = useMemo(() => {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const paying = subMetrics.payingClients

    const newThisMonth = clients.filter(c => c.registrationDate && new Date(c.registrationDate) >= startOfMonth).length
    const churnRisk = clients.filter(c => c.subscriptionActive && !c.isTrial && !c.isWhitelisted && !c.hasActiveDevices)
    const trialsExpiringSoon = clients.filter(c => c.isTrial && !c.trialExpired && c.trialDaysRemaining !== null && c.trialDaysRemaining <= 7)
      .sort((a, b) => (a.trialDaysRemaining ?? 99) - (b.trialDaysRemaining ?? 99))
    const conversionRate = subMetrics.convRate

    const allDevices = clients.flatMap(c => (c.devices || []).map(d => ({ ...d, clientName: c.name, clientEmail: c.email })))

    // Invoice-based hardware revenue (paid, non-quote, non-credit-note)
    const paidHardwareInvs = invoices.filter(i =>
      i.status === 'paid' &&
      !(i.invoiceNumber || '').startsWith('PRE-') &&
      !(i.invoiceNumber || '').startsWith('NC-')
    )
    const getInvRevenue = (d, next) =>
      paidHardwareInvs
        .filter(i => { const dt = new Date(i.issueDate || i.createdAt); return dt >= d && dt < next })
        .reduce((s, i) => s + (parseFloat(i.total) || 0), 0)

    // Per-model stats from device registrations (for cost estimation only)
    const devicesByModel = {}
    for (const d of allDevices) {
      const model = d.model || 'GP-BOX'
      if (!devicesByModel[model]) devicesByModel[model] = { count: 0, revenue: 0, cost: 0 }
      const econ = deviceEconomics[model] || { cost: 0, price: 0 }
      devicesByModel[model].count++
      devicesByModel[model].revenue += econ.price
      devicesByModel[model].cost += econ.cost
    }

    // Use actual invoice totals for revenue totals
    const totalDeviceRevenue = paidHardwareInvs.reduce((s, i) => s + (parseFloat(i.total) || 0), 0) ||
      Object.values(devicesByModel).reduce((s, m) => s + m.revenue, 0)
    const totalDeviceCost = Object.values(devicesByModel).reduce((s, m) => s + m.cost, 0)
    const totalDeviceMargin = totalDeviceRevenue - totalDeviceCost

    const salesMonths = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
      const label = d.toLocaleDateString('es-AR', { month: 'short', year: i >= 6 ? '2-digit' : undefined })

      // Revenue: use actual invoiced amounts; fall back to device-based if no invoices
      const invRevenue = getInvRevenue(d, next)
      let devRevenue = 0, cost = 0, units = 0
      const monthDevices = { 'GP-BOX': 0, 'GP-MINI': 0 }
      for (const dev of allDevices) {
        if (!dev.createdAt) continue
        const cd = new Date(dev.createdAt)
        if (cd >= d && cd < next) {
          const model = dev.model || 'GP-BOX'
          const econ = deviceEconomics[model] || { cost: 0, price: 0 }
          devRevenue += econ.price; cost += econ.cost; units++
          if (monthDevices[model] !== undefined) monthDevices[model]++
        }
      }
      const revenue = invRevenue > 0 ? invRevenue : devRevenue
      salesMonths.push({ label, revenue, cost, margin: revenue - cost, units, ...monthDevices })
    }
    const salesMonthMax = Math.max(...salesMonths.map(m => m.revenue), 1)

    const months = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
      months.push({
        label: d.toLocaleDateString('es-AR', { month: 'short' }),
        count: clients.filter(c => { if (!c.registrationDate) return false; const rd = new Date(c.registrationDate); return rd >= d && rd < next }).length
      })
    }
    const monthMax = Math.max(...months.map(m => m.count), 1)

    // This-month invoice revenue
    const revenueThisMonth = getInvRevenue(startOfMonth, new Date(now.getFullYear(), now.getMonth() + 1, 1)) ||
      allDevices.filter(d => d.createdAt && new Date(d.createdAt) >= startOfMonth)
        .reduce((sum, d) => sum + (deviceEconomics[d.model || 'GP-BOX']?.price || 0), 0)
    const devicesSoldThisMonth = paidHardwareInvs.filter(i => new Date(i.issueDate || i.createdAt) >= startOfMonth).length ||
      allDevices.filter(d => d.createdAt && new Date(d.createdAt) >= startOfMonth).length
    const totalLifetimeRevenue = clients.reduce((sum, c) => sum + getClientBilling(c).total, 0)

    const clientRanking = clients.map(c => {
      const billing = getClientBilling(c)
      return { name: c.name || c.email || 'Sin nombre', email: c.email || '', plan: c.plan || 'free', deviceCount: c.deviceCount || 0,
        devRevenue: billing.devRevenue, devCost: billing.devCost, subRevenue: billing.subRevenue, total: billing.total,
        margin: billing.devRevenue - billing.devCost + billing.subRevenue, isActive: c.subscriptionActive, isTrial: c.isTrial, isWhitelisted: c.isWhitelisted }
    }).filter(c => c.total > 0).sort((a, b) => b.total - a.total)

    return { mrr: subMetrics.mrr, newThisMonth, churnRisk, trialsExpiringSoon, conversionRate,
      planBreakdown: subMetrics.planBreakdown, months, monthMax,
      devicesByModel, totalDeviceRevenue, totalDeviceCost, totalDeviceMargin,
      salesMonths, salesMonthMax, devicesSoldThisMonth, revenueThisMonth,
      totalDevices: allDevices.length, totalLifetimeRevenue, clientRanking }
  }, [clients, invoices, subMetrics, getClientBilling, deviceEconomics])

  // ── Analytics (migrated from ClientsHub analytics) ──
  const analytics = useMemo(() => {
    const now = new Date()
    const paying = subMetrics.payingClients

    const cohorts = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
      const label = d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' })
      const cohortClients = clients.filter(c => { if (!c.registrationDate) return false; const rd = new Date(c.registrationDate); return rd >= d && rd < next })
      const total = cohortClients.length
      const stillActive = cohortClients.filter(c => c.subscriptionActive).length
      cohorts.push({ label, total, stillActive, retention: total > 0 ? Math.round((stillActive / total) * 100) : 0 })
    }

    const avgDevicesPerClient = clients.length > 0 ? (clients.reduce((s, c) => s + (c.deviceCount || 0), 0) / clients.length) : 0
    const avgRevenuePerClient = paying.length > 0 ? (paying.reduce((s, c) => s + getClientBilling(c).total, 0) / paying.length) : 0
    const avgMRRPerClient = paying.length > 0 ? (subMetrics.mrr / paying.length) : 0
    const avgLifetimeMonths = paying.length > 0 ? paying.reduce((s, c) => {
      if (!c.registrationDate) return s + 1
      return s + Math.max(1, (now.getFullYear() - new Date(c.registrationDate).getFullYear()) * 12 + now.getMonth() - new Date(c.registrationDate).getMonth())
    }, 0) / paying.length : 1
    const ltv = avgMRRPerClient * avgLifetimeMonths
    const avgDeviceCost = paying.length > 0 ? paying.reduce((s, c) => s + getClientBilling(c).devCost, 0) / paying.length : 0
    const avgDeviceRevPerClientBiz = paying.length > 0 ? paying.reduce((s, c) => s + getClientBilling(c).devRevenue, 0) / paying.length : 0
    const ltvCacRatio = avgDeviceCost > 0 ? (ltv / avgDeviceCost) : 0
    // Payback: costo neto de hardware (descontando lo que el cliente ya pagó por el equipo) / MRR
    const netHwCost = Math.max(0, avgDeviceCost - avgDeviceRevPerClientBiz)
    const paybackMonths = avgMRRPerClient > 0 ? Math.ceil(netHwCost / avgMRRPerClient) : 0
    const churnedLast30 = clients.filter(c => c.isExpired && c.lastPaymentDate && (now - new Date(c.lastPaymentDate)) < 30 * 24 * 3600 * 1000).length
    const churnRate = paying.length + churnedLast30 > 0 ? Math.round((churnedLast30 / (paying.length + churnedLast30)) * 100) : 0

    const clientsWithHealth = clients.map(c => {
      let score = 0
      if (c.hasActiveDevices) score += 30
      const onlineRatio = c.deviceCount > 0 ? (c.activeDeviceCount || 0) / c.deviceCount : 0
      score += Math.round(onlineRatio * 20)
      if (c.subscriptionActive && !c.isTrial && !c.isWhitelisted) score += 20
      if (c.registrationDate) { const ageMonths = (now.getFullYear() - new Date(c.registrationDate).getFullYear()) * 12 + now.getMonth() - new Date(c.registrationDate).getMonth(); if (ageMonths >= 3) score += 10 }
      if (c.deviceCount >= 2) score += 10
      if (!c.isExpired && !c.trialExpired) score += 10
      return { ...c, healthScore: Math.min(100, score) }
    }).sort((a, b) => a.healthScore - b.healthScore)

    const avgHealthScore = clientsWithHealth.length > 0 ? Math.round(clientsWithHealth.reduce((s, c) => s + c.healthScore, 0) / clientsWithHealth.length) : 0
    const healthDistribution = {
      critical: clientsWithHealth.filter(c => c.healthScore < 30).length,
      atRisk: clientsWithHealth.filter(c => c.healthScore >= 30 && c.healthScore < 60).length,
      healthy: clientsWithHealth.filter(c => c.healthScore >= 60 && c.healthScore < 80).length,
      thriving: clientsWithHealth.filter(c => c.healthScore >= 80).length,
    }

    // MRR history — 6 months for chart
    const mrrHistory = []
    const mrrChartData = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
      const label = d.toLocaleDateString('es-AR', { month: 'short' })
      const monthPaying = clients.filter(c => {
        if (c.isTrial || c.isWhitelisted) return false
        if (!c.subscriptionActive && c.mercadopagoStatus !== 'authorized') return false
        // Use lastPaymentDate as subscription start hint when available
        const subStartDate = c.lastPaymentDate ? new Date(Math.min(new Date(c.lastPaymentDate).getTime(), new Date(c.registrationDate || c.lastPaymentDate).getTime()))
          : c.registrationDate ? new Date(c.registrationDate) : null
        if (!subStartDate) return true
        return subStartDate < next
      })
      const monthMRR = monthPaying.reduce((s, c) => {
        const effectivePlan = (!c.subscriptionActive && c.previousPlan && c.previousPlan !== 'free') ? c.previousPlan : c.plan
        const plan = effectivePlan !== 'free' ? effectivePlan : 'pro'
        const monthly = planPrices[plan] || planPrices.pro
        if (c.billingCycle === 'annual') {
          const annual = ANNUAL_PLAN_PRICES[plan] || monthly * 12
          return s + Math.round(annual / 12)
        }
        return s + monthly
      }, 0)
      mrrHistory.push(monthMRR)
      // Paying count for this month
      const monthTrials = clients.filter(c => { if (!c.isTrial || c.trialExpired) return false; if (!c.registrationDate) return true; return new Date(c.registrationDate) < next }).length
      mrrChartData.push({ month: label, mrr: monthMRR, clientes: monthPaying.length, trials: monthTrials })
    }
    // Growth rate: CAGR as primary, with fallbacks for edge cases
    const firstNonZeroIdx = mrrHistory.findIndex(v => v > 0)
    const lastMRR = mrrHistory[mrrHistory.length - 1]
    let mrrGrowthRate = 0
    if (firstNonZeroIdx >= 0 && firstNonZeroIdx < mrrHistory.length - 1 && mrrHistory[firstNonZeroIdx] > 0 && lastMRR > 0) {
      const periods = mrrHistory.length - 1 - firstNonZeroIdx
      mrrGrowthRate = periods > 0 ? Math.pow(lastMRR / mrrHistory[firstNonZeroIdx], 1 / periods) - 1 : 0
    }
    // Fallback: if CAGR = 0% but we have MRR, try month-over-month changes
    if (mrrGrowthRate === 0 && lastMRR > 0) {
      const changes = []
      for (let j = 1; j < mrrHistory.length; j++) {
        if (mrrHistory[j] > 0 && mrrHistory[j-1] > 0 && mrrHistory[j] !== mrrHistory[j-1]) {
          changes.push((mrrHistory[j] - mrrHistory[j-1]) / mrrHistory[j-1])
        }
      }
      if (changes.length > 0) {
        mrrGrowthRate = changes.reduce((s, v) => s + v, 0) / changes.length
      } else if (firstNonZeroIdx >= 0) {
        // MRR appeared from 0 — estimate linear growth from ramp-up
        const monthsActive = mrrHistory.length - firstNonZeroIdx
        if (monthsActive >= 2) {
          mrrGrowthRate = (lastMRR / monthsActive) / lastMRR
        }
      }
    }
    // Cap to reasonable bounds (max 25%/month — realistic SaaS early stage)
    mrrGrowthRate = Math.max(-0.5, Math.min(0.25, mrrGrowthRate))
    // Dampened forecast: growth rate decays toward 5%/month over time (regression to mean)
    const dampenedRate = (month) => {
      const floor = 0.05
      const decay = 0.85 // each month retains 85% of excess growth over floor
      return floor + (mrrGrowthRate - floor) * Math.pow(decay, month - 1)
    }
    const forecast = [3, 6, 12].map(months => {
      let projMRR = subMetrics.mrr
      let totalRev = 0
      for (let i = 1; i <= months; i++) {
        projMRR = projMRR * (1 + dampenedRate(i))
        totalRev += projMRR
      }
      return { months, projectedMRR: Math.round(projMRR), projectedRevenue: Math.round(totalRev) }
    })

    // Forecast chart data: past 6 + 6 projected (with dampening)
    const forecastChartData = mrrChartData.map(d => ({ month: d.month, mrr: d.mrr, projected: null }))
    let projBase = subMetrics.mrr
    for (let i = 1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
      const label = d.toLocaleDateString('es-AR', { month: 'short' })
      projBase = projBase * (1 + dampenedRate(i))
      forecastChartData.push({ month: label, mrr: null, projected: Math.round(projBase) })
    }
    // Bridge: last known MRR also as first projected point
    if (forecastChartData.length > 6) forecastChartData[5].projected = forecastChartData[5].mrr

    // Revenue composition chart (12 months): equipment + subscriptions
    const revenueComposition = bizMetrics.salesMonths.map((m, i) => {
      // Estimate subscription revenue for that month
      const monthIdx = 11 - i
      const subRev = mrrChartData[Math.max(0, Math.min(5, 5 - Math.floor(monthIdx / 2)))]?.mrr || subMetrics.mrr
      return { month: m.label, equipos: m.revenue, suscripciones: subRev, margen: m.margin }
    })

    const trialsActive = clients.filter(c => c.isTrial && !c.trialExpired).length
    const projectedConversions = Math.round(trialsActive * (subMetrics.convRate / 100))
    const projectedNewMRR = projectedConversions * (planPrices.pro || 9990)

    // ── LTGP (Lifetime Gross Profit) ──
    // LTV solo cuenta revenue de suscripciones; LTGP suma el margen bruto de equipos por cliente
    // LTGP = Subscription GP (≈LTV, ~100% margen) + Device GP (revenue - costo)
    const avgDeviceRevPerClient = paying.length > 0
      ? paying.reduce((s, c) => s + getClientBilling(c).devRevenue, 0) / paying.length
      : 0
    const avgDeviceGP = avgDeviceRevPerClient - avgDeviceCost
    const ltgp = Math.round(ltv + avgDeviceGP)
    const ltgpCacRatio = avgDeviceCost > 0 ? (ltgp / avgDeviceCost).toFixed(1) : '0.0'
    const subGPShare = ltgp > 0 ? Math.round((ltv / ltgp) * 100) : 100
    const devGPShare = ltgp > 0 ? Math.round((avgDeviceGP / ltgp) * 100) : 0
    // Blended gross margin %: how much of total lifetime revenue becomes gross profit
    const lifetimeTotalRev = ltv + avgDeviceRevPerClient
    const blendedGrossMarginPct = lifetimeTotalRev > 0
      ? Math.round((ltgp / lifetimeTotalRev) * 100) : 100

    // ── MRR Movement (variación mes a mes) ──
    const mrrMovement = mrrChartData.map((d, i) => {
      const prev = i > 0 ? mrrChartData[i - 1].mrr : d.mrr
      const delta = d.mrr - prev
      return {
        month: d.month,
        newMRR: Math.max(0, delta),
        churnMRR: Math.min(0, delta),
        net: delta,
        total: d.mrr,
      }
    })

    // ── Gross Margin trend (6 meses) ──
    const grossMarginTrend = bizMetrics.salesMonths.slice(-6).map((m, i) => {
      const subRev = mrrChartData[i]?.mrr || 0
      const hwGP = m.margin
      const totalRev = m.revenue + subRev
      const totalGP = hwGP + subRev
      return {
        month: m.label,
        hwMarginPct: m.revenue > 0 ? Math.round((hwGP / m.revenue) * 100) : 0,
        blendedPct: totalRev > 0 ? Math.round((totalGP / totalRev) * 100) : 0,
        hwRev: m.revenue,
        subRev,
      }
    })

    // ── SaaS Mix trend (% revenue recurrente vs hardware) ──
    const saasMixTrend = bizMetrics.salesMonths.slice(-6).map((m, i) => {
      const subRev = mrrChartData[i]?.mrr || 0
      const total = m.revenue + subRev
      return {
        month: m.label,
        saasPct: total > 0 ? Math.round((subRev / total) * 100) : 0,
        hwPct: total > 0 ? Math.round((m.revenue / total) * 100) : 0,
        subRev,
        hwRev: m.revenue,
        total,
      }
    })

    // ── ARPU trend (6 meses) ──
    const arpuTrend = mrrChartData.map(d => ({
      month: d.month,
      arpu: d.clientes > 0 ? Math.round(d.mrr / d.clientes) : 0,
    }))

    // ── Rule of 40 ──
    // Regla de las 40: Growth Rate % + Gross Margin % ≥ 40 → negocio en buen estado
    const mrrGrowthRatePct = Math.round(mrrGrowthRate * 100)
    const rule40Score = mrrGrowthRatePct + blendedGrossMarginPct
    const rule40Pass = rule40Score >= 40

    return { cohorts,
      avgDevicesPerClient: avgDevicesPerClient.toFixed(1), avgRevenuePerClient: Math.round(avgRevenuePerClient),
      avgMRRPerClient: Math.round(avgMRRPerClient), ltv: Math.round(ltv), avgDeviceCost: Math.round(avgDeviceCost),
      ltvCacRatio: ltvCacRatio.toFixed(1), paybackMonths, churnRate, avgLifetimeMonths: Math.round(avgLifetimeMonths),
      clientsWithHealth, avgHealthScore, healthDistribution, forecast,
      mrrGrowthRate: mrrGrowthRatePct, projectedConversions, projectedNewMRR,
      mrrChartData, forecastChartData, revenueComposition,
      // LTGP
      ltgp, ltgpCacRatio, subGPShare, devGPShare, blendedGrossMarginPct,
      avgDeviceRevPerClient: Math.round(avgDeviceRevPerClient), avgDeviceGP: Math.round(avgDeviceGP),
      // Charts
      mrrMovement, grossMarginTrend, saasMixTrend, arpuTrend,
      // Rule of 40
      rule40Score, rule40Pass, mrrGrowthRatePct,
    }
  }, [clients, subMetrics, getClientBilling, planPrices, bizMetrics])

  // ── Invoice Metrics ──
  const invMetrics = useMemo(() => {
    const now = new Date()
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    const paid = invoices.filter(i => i.status === 'paid')
    const pending = invoices.filter(i => i.status === 'sent' || i.status === 'draft')
    const overdue = invoices.filter(i => i.status === 'overdue' || (i.status === 'sent' && i.dueDate && new Date(i.dueDate) < now))
    const monthInv = invoices.filter(i => (i.issueDate || '').startsWith(thisMonth))
    const totalPaid = paid.reduce((s, i) => s + (parseFloat(i.total) || 0), 0)
    const totalPending = pending.reduce((s, i) => s + (parseFloat(i.total) || 0), 0)
    const totalOverdue = overdue.reduce((s, i) => s + (parseFloat(i.total) || 0), 0)
    const monthTotal = monthInv.reduce((s, i) => s + (parseFloat(i.total) || 0), 0)
    const monthlyData = []
    for (let m = 5; m >= 0; m--) {
      const d = new Date(now.getFullYear(), now.getMonth() - m, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = d.toLocaleDateString('es-AR', { month: 'short' })
      monthlyData.push({ month: label, revenue: paid.filter(i => (i.issueDate || '').startsWith(key)).reduce((s, i) => s + (parseFloat(i.total) || 0), 0) })
    }
    return { totalPaid, totalPending, totalOverdue, monthTotal, overdue: overdue.length, pending: pending.length, paid: paid.length, monthlyData }
  }, [invoices])

  // ── Cash Flow ──
  const cashFlow = useMemo(() => {
    const now = new Date(); const data = []
    const mrrData = analytics?.mrrChartData || []
    for (let m = 5; m >= 0; m--) {
      const d = new Date(now.getFullYear(), now.getMonth() - m, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = d.toLocaleDateString('es-AR', { month: 'short' })
      const income = receipts.filter(r => (r.date || r.createdAt || '').startsWith(key)).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
      // Use per-month MRR from analytics instead of current MRR for all months
      const monthMRR = mrrData[5 - m]?.mrr ?? 0
      data.push({ month: label, ingresos: income + monthMRR, suscripciones: monthMRR, facturado: income })
    }
    return data
  }, [receipts, analytics])

  // ── Cash Flow Allocation (Distribución del flujo de caja) ──
  const cashAllocation = useMemo(() => {
    const salesLast6 = bizMetrics.salesMonths.slice(-6)
    const mrrData = analytics.mrrChartData

    // Current month base values
    const curDevRev = salesLast6[salesLast6.length - 1]?.revenue || 0
    const curDevCost = salesLast6[salesLast6.length - 1]?.cost || 0
    const curMRR = subMetrics.mrr

    // Step 1: INGRESO TOTAL (each component overridable separately)
    const realIncome = curDevRev + curMRR
    const effectiveDevRev = simDevRev !== null ? simDevRev : curDevRev
    const effectiveMRR = simMRR !== null ? simMRR : curMRR
    const totalIncome = effectiveDevRev + effectiveMRR

    // Step 2: - REPOS. INVENTARIO (overridable) — includes production cost + growth stock
    const growthMult = analytics.mrrGrowthRate > 0 ? 1 + (analytics.mrrGrowthRate / 100) : 1
    const realRepos = Math.round(curDevCost * growthMult)
    const inventoryReserve = simRepos !== null ? simRepos : realRepos
    const afterRepos = totalIncome - inventoryReserve

    // Step 3: - IMPUESTOS (% adjustable via taxRate)
    const tax = Math.max(0, Math.round(afterRepos * (taxRate / 100)))
    const afterTax = afterRepos - tax

    // Step 5: - GASTOS OPERATIVOS
    const opex = monthlyOpex
    const ganancia = afterTax - opex

    // Step 6: Repartir GANANCIA — comisiones sobre precio de venta (devRev)
    const armado3dAmt  = Math.round(effectiveDevRev * armado3dPct  / 100)
    const armadoElecAmt = Math.round(effectiveDevRev * armadoElecPct / 100)
    const ventaCommAmt  = Math.round(effectiveDevRev * ventaPct     / 100)
    const commission = armado3dAmt + armadoElecAmt + ventaCommAmt
    const reinvestment = Math.max(0, Math.round(ganancia * (reinvestPct / 100)))
    const freeCash = Math.max(0, ganancia - commission - reinvestment)

    const grossMarginPct = totalIncome > 0 ? Math.round(((totalIncome - inventoryReserve) / totalIncome) * 100) : 0

    // Pie data
    const allocationPie = [
      { name: 'Repos. Inventario', value: inventoryReserve, color: '#f59e0b' },
      { name: 'Impuestos', value: tax, color: '#ef4444' },
      ...(opex > 0 ? [{ name: 'Gastos Op.', value: opex, color: '#64748b' }] : []),
      { name: 'Comisión', value: commission, color: '#ec4899' },
      { name: 'Reinversión', value: reinvestment, color: '#8b5cf6' },
      { name: 'Ganancia Neta', value: freeCash, color: '#10b981' },
    ].filter(d => d.value > 0)

    // 6-month trend for stacked bar (same new logic)
    const monthlyTrend = salesLast6.map((m, i) => {
      const mrr = mrrData[i]?.mrr || curMRR
      const inc = m.revenue + mrr
      const mRepos = Math.round(m.cost * growthMult)
      const mAfterRepos = inc - mRepos
      const mTax = Math.max(0, Math.round(mAfterRepos * (taxRate / 100)))
      const mGanancia = mAfterRepos - mTax - opex
      const mComm = Math.round(m.revenue * (armado3dPct + armadoElecPct + ventaPct) / 100)
      const mReinv = Math.max(0, Math.round(mGanancia * (reinvestPct / 100)))
      const mFree = Math.max(0, mGanancia - mComm - mReinv)
      return { month: m.label, inventario: mRepos, impuestos: mTax, comision: mComm, reinversion: mReinv, ganancia: mFree, total: inc }
    })

    // Monthly snapshots for Sankey (last 5 months, excluding current)
    const monthlySnapshots = salesLast6.slice(0, -1).map((m, i) => {
      const mrr = mrrData[i]?.mrr || 0
      return { label: m.label, devRev: m.revenue, devCost: Math.round(m.cost * growthMult), mrr }
    })

    // Recommendations
    const recs = []
    const reposPct = totalIncome > 0 ? Math.round((inventoryReserve / totalIncome) * 100) : 0
    if (reposPct > 50) recs.push({ type: 'warning', text: `Repos. Inventario es ${reposPct}% del ingreso — considerar optimizar costos de producción o aumentar precios.` })
    if (analytics.mrrGrowthRate > 10) recs.push({ type: 'growth', text: `Crecimiento ${analytics.mrrGrowthRate}%/mes — considerar reinversión agresiva (${Math.min(50, reinvestPct + 10)}%) para capitalizar el momento.` })
    if (analytics.mrrGrowthRate < 0) recs.push({ type: 'warning', text: `MRR decreciente — priorizar retención y reducir reinversión hasta estabilizar.` })
    if (inventoryReserve > afterTax * 0.5 && afterTax > 0) recs.push({ type: 'info', text: 'Reposición de inventario consume >50% del beneficio post-tax — evaluar eficiencia de producción.' })
    if (freeCash <= 0 && totalIncome > 0) recs.push({ type: 'danger', text: 'Cash flow neto negativo — las obligaciones superan los ingresos disponibles este mes.' })
    if (Number(analytics.ltvCacRatio) >= 3 && freeCash > 0) recs.push({ type: 'growth', text: `LTV/CAC de ${analytics.ltvCacRatio}x es excelente — buen momento para escalar adquisición de clientes.` })
    const subPct = totalIncome > 0 ? Math.round((curMRR / totalIncome) * 100) : 0
    if (subPct < 40 && totalIncome > 0) recs.push({ type: 'info', text: `Suscripciones son solo ${subPct}% del ingreso — aumentar base recurrente reduce dependencia de ventas puntuales.` })
    const isSimulating = simDevRev !== null || simMRR !== null || simRepos !== null

    return {
      totalIncome, realIncome, curMRR, curDevRev,
      effectiveDevRev, effectiveMRR,
      inventoryReserve, realRepos, afterRepos, grossMarginPct,
      tax, afterTax, opex, ganancia,
      commission, armado3dAmt, armadoElecAmt, ventaCommAmt,
      reinvestment, freeCash,
      allocationPie, monthlyTrend, monthlySnapshots, recs,
      reposPct, subPct, isSimulating,
      netMarginPct: totalIncome > 0 ? Math.round((freeCash / totalIncome) * 100) : 0,
    }
  }, [bizMetrics, subMetrics, analytics, taxRate, armado3dPct, armadoElecPct, ventaPct, reinvestPct, monthlyOpex, simDevRev, simMRR, simRepos])

  const tabs = [
    { key: 'overview', icon: BarChart2, label: 'Resumen' },
    { key: 'ventas', icon: Package, label: 'Ventas' },
    { key: 'analytics', icon: PieChart, label: 'Analytics' },
    { key: 'flujo-caja', icon: DollarSign, label: 'Flujo de Caja' },
    { key: 'mercadopago', icon: Wallet, label: 'MercadoPago' },
  ]

  return (
    <div className="fin-page">
      <div className="fin-header">
        <div className="fin-title-row">
          <DollarSign size={28} />
          <div>
            <h1>Dashboard Financiero</h1>
            <p className="fin-subtitle">Consolidado de ingresos, suscripciones, ventas y analytics</p>
          </div>
        </div>
        <button className="fin-btn primary" onClick={loadData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
        </button>
      </div>

      <div className="fin-tabs">
        {tabs.map(t => (
          <button key={t.key} className={`fin-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="fin-loading"><RefreshCw size={20} className="spin" /> Cargando datos financieros...</div>
      ) : (
        <>
          {/* ── Resumen ── */}
          {tab === 'overview' && (
            <>
              {/* KPIs */}
              <div className="fin-kpis">
                <div className="fin-kpi accent">
                  <DollarSign size={20} />
                  <div><span className="fin-kpi-value">{fmt(subMetrics.mrr)}</span><span className="fin-kpi-label">MRR</span></div>
                </div>
                <div className="fin-kpi">
                  <TrendingUp size={20} />
                  <div><span className="fin-kpi-value">{fmt(subMetrics.arr)}</span><span className="fin-kpi-label">ARR</span></div>
                </div>
                <div className="fin-kpi">
                  <Target size={20} />
                  <div><span className="fin-kpi-value">{fmt(subMetrics.arpu)}</span><span className="fin-kpi-label">ARPU</span></div>
                </div>
                <div className="fin-kpi">
                  <Users size={20} />
                  <div><span className="fin-kpi-value">{subMetrics.paying}</span><span className="fin-kpi-label">Pagando</span></div>
                </div>
                <div className="fin-kpi">
                  <CreditCard size={20} />
                  <div><span className="fin-kpi-value">{subMetrics.convRate}%</span><span className="fin-kpi-label">Conversión</span></div>
                </div>
                <div className="fin-kpi warning">
                  <Clock size={20} />
                  <div><span className="fin-kpi-value">{fmt(invMetrics.totalPending)}</span><span className="fin-kpi-label">Pendiente</span></div>
                </div>
                <div className="fin-kpi danger">
                  <AlertTriangle size={20} />
                  <div><span className="fin-kpi-value">{fmt(invMetrics.totalOverdue)}</span><span className="fin-kpi-label">Vencido</span></div>
                </div>
              </div>

              {/* Plan Cards */}
              <div className="fin-plan-cards">
                {subMetrics.planBreakdown.map(p => (
                  <div key={p.name} className="fin-plan-card" style={{ borderTopColor: p.color }}>
                    <h4>{p.name}</h4>
                    <div className="fin-plan-count">{p.count} <span>clientes</span></div>
                    {p.annualCount > 0 && (
                      <div className="fin-plan-billing-split">
                        {p.monthlyCount > 0 && <span>{p.monthlyCount} mensual</span>}
                        {p.monthlyCount > 0 && p.annualCount > 0 && <span className="fin-plan-split-sep">·</span>}
                        <span className="fin-plan-annual-badge">{p.annualCount} anual</span>
                      </div>
                    )}
                    <div className="fin-plan-rev">{fmt(p.revenue)} <span>/mes</span></div>
                  </div>
                ))}
              </div>

              {/* MRR Trend + Plan Distribution */}
              <div className="fin-charts-row">
                <div className="fin-chart-card">
                  <h3><TrendingUp size={16} /> Evolución MRR (6 meses)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={analytics.mrrChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" fontSize={12} />
                      <Tooltip formatter={(v, name) => [name === 'MRR' ? fmt(v) : v, name]}
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="mrr" stroke="#10b981" fill="#10b98115" strokeWidth={2} name="MRR" />
                      <Line yAxisId="right" type="monotone" dataKey="clientes" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} name="Pagando" />
                      <Line yAxisId="right" type="monotone" dataKey="trials" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} name="Trials" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="fin-chart-card">
                  <h3>Distribución por Plan</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <RePie>
                      <Pie data={subMetrics.planBreakdown.filter(p => p.count > 0)} dataKey="revenue" nameKey="name" cx="50%" cy="50%"
                        innerRadius={50} outerRadius={80} paddingAngle={3}
                        label={({ name, value }) => `${name}: ${fmt(value)}`}>
                        {subMetrics.planBreakdown.map((entry, i) => <Cell key={i} fill={entry.color || PIE_COLORS[i]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                    </RePie>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Conversion Funnel */}
              <div className="fin-funnel">
                <h3>Embudo de Conversión</h3>
                <div className="fin-funnel-bars">
                  <div className="fin-funnel-bar"><span className="fin-funnel-label">Total Registrados</span><div className="fin-funnel-fill" style={{ width: '100%', background: '#334155' }}>{clients.length}</div></div>
                  <div className="fin-funnel-bar"><span className="fin-funnel-label">En Trial</span><div className="fin-funnel-fill" style={{ width: `${(subMetrics.trials / (clients.length || 1)) * 100}%`, background: '#f59e0b' }}>{subMetrics.trials}</div></div>
                  <div className="fin-funnel-bar"><span className="fin-funnel-label">Pagando</span><div className="fin-funnel-fill" style={{ width: `${(subMetrics.paying / (clients.length || 1)) * 100}%`, background: '#10b981' }}>{subMetrics.paying}</div></div>
                  <div className="fin-funnel-bar"><span className="fin-funnel-label">Trial Expirado</span><div className="fin-funnel-fill" style={{ width: `${(subMetrics.expired / (clients.length || 1)) * 100}%`, background: '#ef4444' }}>{subMetrics.expired}</div></div>
                </div>
              </div>

              {/* Row 3: Revenue Composition */}
              <div className="fin-charts-row">
                <div className="fin-chart-card fin-chart-wide">
                  <h3><BarChart2 size={16} /> Composición de Revenue (12 meses)</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={analytics.revenueComposition}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      <Legend />
                      <Bar dataKey="equipos" fill="#3b82f6" name="Equipos" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="suscripciones" fill="#8b5cf6" name="Suscripciones" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="margen" stroke="#10b981" strokeWidth={2} dot={false} name="Margen Equipos" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Row 4: Cash Flow */}
              <div className="fin-charts-row">
                <div className="fin-chart-card fin-chart-wide">
                  <h3><DollarSign size={16} /> Flujo de Caja (6 meses)</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={cashFlow}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      <Legend />
                      <Bar dataKey="facturado" fill="#3b82f6" name="Facturado/Cobrado" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="suscripciones" fill="#8b5cf6" name="Suscripciones" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="ingresos" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} name="Total Ingresos" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* ── Ventas ── */}
          {tab === 'ventas' && (
            <div className="biz-page">
              {/* KPI Cards */}
              <div className="biz-kpis">
                <div className="biz-kpi">
                  <span className="biz-kpi-icon" style={{ background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}><Package size={18} /></span>
                  <div>
                    <div className="biz-kpi-value">{fmt(bizMetrics.totalDeviceRevenue)}</div>
                    <div className="biz-kpi-label">Revenue equipos</div>
                    <div className="biz-kpi-sub">{bizMetrics.totalDevices} vendidos total</div>
                  </div>
                </div>
                <div className="biz-kpi">
                  <span className="biz-kpi-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}><TrendingUp size={18} /></span>
                  <div>
                    <div className="biz-kpi-value">{fmt(bizMetrics.totalDeviceMargin)}</div>
                    <div className="biz-kpi-label">Margen equipos</div>
                    <div className="biz-kpi-sub">{bizMetrics.totalDeviceRevenue > 0 ? Math.round((bizMetrics.totalDeviceMargin / bizMetrics.totalDeviceRevenue) * 100) : 0}%</div>
                  </div>
                </div>
                <div className="biz-kpi">
                  <span className="biz-kpi-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}><DollarSign size={18} /></span>
                  <div>
                    <div className="biz-kpi-value">{fmt(bizMetrics.totalLifetimeRevenue)}</div>
                    <div className="biz-kpi-label">Lifetime revenue</div>
                    <div className="biz-kpi-sub">Equipos + suscripciones</div>
                  </div>
                </div>
                <div className="biz-kpi">
                  <span className="biz-kpi-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}><Users size={18} /></span>
                  <div>
                    <div className="biz-kpi-value">{bizMetrics.newThisMonth}</div>
                    <div className="biz-kpi-label">Clientes nuevos (mes)</div>
                  </div>
                </div>
                <div className="biz-kpi">
                  <span className="biz-kpi-icon" style={{ background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}><Package size={18} /></span>
                  <div>
                    <div className="biz-kpi-value">{bizMetrics.devicesSoldThisMonth}</div>
                    <div className="biz-kpi-label">Devices vendidos (mes)</div>
                    <div className="biz-kpi-sub">{fmt(bizMetrics.revenueThisMonth)} revenue</div>
                  </div>
                </div>
              </div>

              <div className="biz-grid">
                {/* Device Sales Breakdown */}
                <div className="biz-card">
                  <h3 className="biz-card-title"><Package size={15} /> Ventas por Modelo</h3>
                  <div className="biz-plans">
                    {Object.entries(bizMetrics.devicesByModel).map(([model, data]) => (
                      <div key={model} className="biz-plan-row">
                        <div className="biz-plan-info">
                          <span className="biz-plan-dot" style={{ background: model === 'GP-BOX' ? '#3b82f6' : '#10b981' }} />
                          <span className="biz-plan-name">{model}</span>
                          <span className="biz-plan-count">({data.count})</span>
                        </div>
                        <div className="biz-device-detail">
                          <span className="biz-detail-cost">C: {fmt(data.cost)}</span>
                          <span className="biz-detail-price">V: {fmt(data.revenue)}</span>
                          <span className="biz-detail-margin">M: {fmt(data.revenue - data.cost)}</span>
                        </div>
                      </div>
                    ))}
                    {Object.keys(bizMetrics.devicesByModel).length > 0 && (
                      <div className="biz-plan-row biz-totals-row">
                        <div className="biz-plan-info"><span className="biz-plan-name" style={{ fontWeight: 700 }}>Total</span></div>
                        <div className="biz-device-detail">
                          <span className="biz-detail-cost">C: {fmt(bizMetrics.totalDeviceCost)}</span>
                          <span className="biz-detail-price">V: {fmt(bizMetrics.totalDeviceRevenue)}</span>
                          <span className="biz-detail-margin">M: {fmt(bizMetrics.totalDeviceMargin)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 12-month Sales Chart */}
                <div className="biz-card biz-card-wide">
                  <h3 className="biz-card-title"><BarChart2 size={15} /> Ventas de Equipos (12 meses)</h3>
                  <div className="biz-sales-chart">
                    {bizMetrics.salesMonths.map((m, i) => (
                      <div key={i} className="biz-bar-col">
                        <span className="biz-bar-val">{m.units > 0 ? m.units : ''}</span>
                        <div className="biz-bar-stack" style={{ height: `${Math.max(4, (m.revenue / bizMetrics.salesMonthMax) * 100)}%` }}>
                          {m['GP-BOX'] > 0 && <div className="biz-bar-segment" style={{ flex: m['GP-BOX'], background: '#3b82f6' }} />}
                          {m['GP-MINI'] > 0 && <div className="biz-bar-segment" style={{ flex: m['GP-MINI'], background: '#10b981' }} />}
                          {m.units === 0 && <div className="biz-bar-segment" style={{ flex: 1, background: '#334155' }} />}
                        </div>
                        <span className="biz-bar-label">{m.label}</span>
                        <span className="biz-bar-revenue">{m.revenue > 0 ? fmt(m.revenue) : ''}</span>
                      </div>
                    ))}
                  </div>
                  <div className="biz-chart-legend">
                    <span className="biz-legend-item"><span className="biz-legend-dot" style={{ background: '#3b82f6' }} /> GP-BOX</span>
                    <span className="biz-legend-item"><span className="biz-legend-dot" style={{ background: '#10b981' }} /> GP-MINI</span>
                  </div>
                </div>

                {/* New Clients Chart */}
                <div className="biz-card">
                  <h3 className="biz-card-title"><Users size={15} /> Nuevos Clientes (6 meses)</h3>
                  <div className="biz-chart">
                    {bizMetrics.months.map((m, i) => (
                      <div key={i} className="biz-bar-col">
                        <span className="biz-bar-val">{m.count || ''}</span>
                        <div className="biz-bar" style={{ height: `${(m.count / bizMetrics.monthMax) * 100}%`, background: '#3b82f6' }} />
                        <span className="biz-bar-label">{m.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sales Detail Table */}
                <div className="biz-card">
                  <h3 className="biz-card-title"><BarChart2 size={15} /> Detalle de Ventas</h3>
                  <div className="biz-sales-table-wrap">
                    <table className="biz-sales-table">
                      <thead><tr><th>Mes</th><th>Uds</th><th>BOX</th><th>MINI</th><th>Revenue</th><th>Costo</th><th>Margen</th></tr></thead>
                      <tbody>
                        {[...bizMetrics.salesMonths].reverse().filter(m => m.units > 0).map((m, i) => (
                          <tr key={i}>
                            <td className="biz-sales-month">{m.label}</td>
                            <td>{m.units}</td>
                            <td>{m['GP-BOX'] || 0}</td>
                            <td>{m['GP-MINI'] || 0}</td>
                            <td className="biz-sales-revenue">{fmt(m.revenue)}</td>
                            <td className="biz-sales-cost">{fmt(m.cost)}</td>
                            <td className="biz-sales-margin">{fmt(m.margin)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Trials Expiring */}
                <div className="biz-card">
                  <h3 className="biz-card-title"><Clock size={15} /> Trials por Vencer (&le;7d)</h3>
                  {bizMetrics.trialsExpiringSoon.length === 0 ? (
                    <div className="biz-empty"><span>Sin trials próximos a vencer</span></div>
                  ) : (
                    <div className="biz-list">
                      {bizMetrics.trialsExpiringSoon.map(c => (
                        <div key={c.accountId} className="biz-list-row">
                          <div>
                            <div className="biz-list-name">{c.name || c.email}</div>
                            <div className="biz-list-sub">{c.deviceCount || 0} device(s)</div>
                          </div>
                          <span className={`biz-expiry-badge ${c.trialDaysRemaining <= 1 ? 'today' : c.trialDaysRemaining <= 3 ? 'urgent' : 'soon'}`}>
                            {c.trialDaysRemaining === 0 ? 'Hoy' : `${c.trialDaysRemaining}d`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Client Profitability Ranking */}
                <div className="biz-card biz-card-wide">
                  <h3 className="biz-card-title"><TrendingUp size={15} /> Ranking de Rentabilidad</h3>
                  <div className="biz-sales-table-wrap">
                    <table className="biz-sales-table">
                      <thead><tr><th>#</th><th>Cliente</th><th>Plan</th><th>Devices</th><th>Equipos</th><th>Suscripción</th><th>Total</th></tr></thead>
                      <tbody>
                        {bizMetrics.clientRanking.slice(0, 15).map((c, i) => (
                          <tr key={i}>
                            <td style={{ color: i < 3 ? '#f59e0b' : '#64748b', fontWeight: 700 }}>{i + 1}</td>
                            <td>
                              <div className="biz-list-name">{c.name}</div>
                              <div className="biz-list-sub">{c.email}</div>
                            </td>
                            <td><span style={{ color: PLANS[c.plan]?.color }}>{PLANS[c.plan]?.icon} {PLANS[c.plan]?.name || c.plan}</span></td>
                            <td>{c.deviceCount}</td>
                            <td className="biz-sales-revenue">{fmt(c.devRevenue)}</td>
                            <td>{fmt(c.subRevenue)}</td>
                            <td style={{ fontWeight: 700, color: '#f1f5f9' }}>{fmt(c.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Analytics (migrated from ClientsHub) ── */}
          {tab === 'analytics' && (
            <div className="an-page">

              {/* ── Scorecard strip ── */}
              <div className="an-scorecard">
                {[
                  { label: 'LTGP', value: fmt(analytics.ltgp), sub: 'GP real/cliente', color: '#a78bfa', icon: <Sparkles size={14} /> },
                  { label: 'LTGP/CAC', value: `${analytics.ltgpCacRatio}x`, sub: '≥3x = excelente', color: Number(analytics.ltgpCacRatio) >= 3 ? '#10b981' : '#f59e0b', icon: <Award size={14} /> },
                  { label: 'LTV', value: fmt(analytics.ltv), sub: `${analytics.avgLifetimeMonths}m promedio`, color: '#10b981', icon: <DollarSign size={14} /> },
                  { label: 'CAC', value: fmt(analytics.avgDeviceCost), sub: 'Costo equipo prom.', color: '#64748b', icon: <Package size={14} /> },
                  { label: 'Payback', value: `${analytics.paybackMonths}m`, sub: `Costo neto HW / MRR`, color: '#f59e0b', icon: <Clock size={14} /> },
                  { label: 'Gross Margin', value: `${analytics.blendedGrossMarginPct}%`, sub: 'Blended lifetime', color: analytics.blendedGrossMarginPct >= 60 ? '#10b981' : '#f59e0b', icon: <Layers size={14} /> },
                  { label: 'Churn', value: `${analytics.churnRate}%`, sub: 'Últimos 30 días', color: analytics.churnRate <= 3 ? '#10b981' : analytics.churnRate <= 8 ? '#f59e0b' : '#ef4444', icon: <TrendingDown size={14} /> },
                  { label: 'Rule of 40', value: String(analytics.rule40Score), sub: `${analytics.mrrGrowthRatePct}% g + ${analytics.blendedGrossMarginPct}% GM`, color: analytics.rule40Pass ? '#10b981' : '#f59e0b', icon: <Target size={14} />, highlight: analytics.rule40Pass },
                  { label: 'Health Score', value: String(analytics.avgHealthScore), sub: `${analytics.healthDistribution.critical} críticos`, color: analytics.avgHealthScore >= 60 ? '#10b981' : '#f59e0b', icon: <Heart size={14} /> },
                ].map(k => (
                  <div key={k.label} className={`an-sc-item${k.highlight ? ' an-sc-pass' : ''}`}>
                    <div className="an-sc-icon" style={{ color: k.color }}>{k.icon}</div>
                    <div className="an-sc-value" style={{ color: k.color }}>{k.value}</div>
                    <div className="an-sc-label">{k.label}</div>
                    <div className="an-sc-sub">{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* ── Sección 1: Unit Economics ── */}
              <div className="an-section">
                <div className="an-section-header">
                  <span className="an-section-pill"><Target size={13} /> Unit Economics</span>
                </div>
                <div className="an-grid-2">
                  <div className="biz-card">
                    <h3 className="biz-card-title"><Target size={15} /> Detalle por Cliente</h3>
                    <div className="an-unit-list">
                      <div className="an-unit-row"><span>Revenue promedio/cliente</span><strong>{fmt(analytics.avgRevenuePerClient)}</strong></div>
                      <div className="an-unit-row"><span>MRR promedio/cliente</span><strong>{fmt(analytics.avgMRRPerClient)}</strong></div>
                      <div className="an-unit-row"><span>Devices promedio/cliente</span><strong>{analytics.avgDevicesPerClient}</strong></div>
                      <div className="an-unit-row"><span>Lifetime promedio</span><strong>{analytics.avgLifetimeMonths} meses</strong></div>
                      <div className="an-unit-row an-unit-sep"><span>LTV</span><strong>{fmt(analytics.ltv)}</strong></div>
                      <div className="an-unit-row an-unit-highlight">
                        <span>LTGP <span className="an-unit-badge">GP real</span></span>
                        <strong style={{ color: '#a78bfa' }}>{fmt(analytics.ltgp)}</strong>
                      </div>
                      <div className="an-unit-row an-unit-indent"><span>↳ Subs GP ({analytics.subGPShare}%)</span><strong>{fmt(analytics.ltv)}</strong></div>
                      <div className="an-unit-row an-unit-indent">
                        <span>↳ Device GP ({analytics.devGPShare}%)</span>
                        <strong style={{ color: analytics.avgDeviceGP >= 0 ? '#10b981' : '#ef4444' }}>{fmt(analytics.avgDeviceGP)}</strong>
                      </div>
                      <div className="an-unit-row"><span>CAC (costo equipo prom.)</span><strong>{fmt(analytics.avgDeviceCost)}</strong></div>
                      <div className="an-unit-row"><span>LTV/CAC</span><strong style={{ color: Number(analytics.ltvCacRatio) >= 3 ? '#10b981' : '#f59e0b' }}>{analytics.ltvCacRatio}x</strong></div>
                      <div className="an-unit-row an-unit-highlight">
                        <span>LTGP/CAC</span>
                        <strong style={{ color: Number(analytics.ltgpCacRatio) >= 3 ? '#10b981' : '#f59e0b' }}>{analytics.ltgpCacRatio}x</strong>
                      </div>
                      <div className="an-unit-row"><span>Payback <span className="an-unit-badge">HW neto / MRR</span></span><strong>{analytics.paybackMonths} meses</strong></div>
                      <div className="an-unit-row">
                        <span>Gross Margin blended</span>
                        <strong style={{ color: analytics.blendedGrossMarginPct >= 60 ? '#10b981' : analytics.blendedGrossMarginPct >= 40 ? '#f59e0b' : '#ef4444' }}>{analytics.blendedGrossMarginPct}%</strong>
                      </div>
                    </div>
                  </div>

                  <div className="biz-card">
                    <h3 className="biz-card-title"><Sparkles size={15} /> Brújula del Negocio</h3>
                    <p className="an-radar-desc">Comparativa de KPIs vs benchmarks SaaS. 100 = benchmark ideal.</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <RadarChart data={[
                        { metric: 'LTGP/CAC', value: Math.min(100, (Number(analytics.ltgpCacRatio) / 5) * 100), benchmark: 60, fullMark: 100 },
                        { metric: 'Gross Margin', value: Math.min(100, analytics.blendedGrossMarginPct), benchmark: 70, fullMark: 100 },
                        { metric: 'Rule of 40', value: Math.min(100, analytics.rule40Score), benchmark: 40, fullMark: 100 },
                        { metric: 'Conversión', value: Math.min(100, subMetrics.convRate * 2), benchmark: 60, fullMark: 100 },
                        { metric: 'Retención', value: Math.min(100, 100 - analytics.churnRate * 3), benchmark: 85, fullMark: 100 },
                        { metric: 'Payback', value: Math.min(100, Math.max(0, 100 - (analytics.paybackMonths / 12) * 100)), benchmark: 50, fullMark: 100 },
                      ]}>
                        <PolarGrid stroke="#334155" />
                        <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar name="Tu negocio" dataKey="value" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.25} />
                        <Radar name="Benchmark" dataKey="benchmark" stroke="#475569" fill="#475569" fillOpacity={0.08} strokeDasharray="4 4" />
                        <Legend />
                        <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                          formatter={(v, name) => [`${Number(v).toFixed(0)}`, name]} />
                      </RadarChart>
                    </ResponsiveContainer>

                    {/* Rule of 40 inline callout */}
                    <div className={`an-rule40 ${analytics.rule40Pass ? 'pass' : 'warn'}`}>
                      <span className="an-rule40-score">{analytics.rule40Score}</span>
                      <div>
                        <strong>Rule of 40 — {analytics.rule40Pass ? '✓ Superada' : '↗ Por alcanzar'}</strong>
                        <span>{analytics.mrrGrowthRatePct}% crecimiento + {analytics.blendedGrossMarginPct}% margen</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Sección 2: Crecimiento MRR ── */}
              <div className="an-section">
                <div className="an-section-header">
                  <span className="an-section-pill"><TrendingUp size={13} /> Crecimiento MRR</span>
                </div>
                <div className="an-grid-2">
                  <div className="biz-card">
                    <h3 className="biz-card-title"><BarChart2 size={15} /> Variación MRR mensual</h3>
                    <p className="an-radar-desc">Nuevo MRR ganado vs perdido. Línea = total acumulado.</p>
                    <ResponsiveContainer width="100%" height={210}>
                      <ComposedChart data={analytics.mrrMovement} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <ReferenceLine y={0} stroke="#475569" strokeWidth={1} />
                        <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                        <Legend iconSize={10} />
                        <Bar dataKey="newMRR" name="+ Nuevo" fill="#10b981" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="churnMRR" name="− Churn" fill="#ef4444" radius={[0, 0, 3, 3]} />
                        <Line type="monotone" dataKey="total" stroke="#a78bfa" strokeWidth={2} dot={{ fill: '#a78bfa', r: 3 }} name="Total" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="biz-card">
                    <h3 className="biz-card-title"><LineChartIcon size={15} /> ARPU mensual</h3>
                    <p className="an-radar-desc">Revenue promedio por cliente pagador.</p>
                    <ResponsiveContainer width="100%" height={210}>
                      <ComposedChart data={analytics.arpuTrend} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                        <Area type="monotone" dataKey="arpu" stroke="#3b82f6" fill="#3b82f615" strokeWidth={2.5} name="ARPU" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="biz-card">
                    <h3 className="biz-card-title"><Layers size={15} /> Gross Margin % mensual</h3>
                    <p className="an-radar-desc">Margen bruto hardware vs margen blended (HW + suscripciones).</p>
                    <ResponsiveContainer width="100%" height={210}>
                      <ComposedChart data={analytics.grossMarginTrend} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                        <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={11} tickFormatter={v => `${v}%`} />
                        <Tooltip formatter={v => `${v}%`} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                        <Legend iconSize={10} />
                        <Bar dataKey="hwMarginPct" name="HW" fill="#3b82f610" stroke="#3b82f6" strokeWidth={1} radius={[3, 3, 0, 0]} />
                        <Line type="monotone" dataKey="blendedPct" name="Blended" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="biz-card">
                    <h3 className="biz-card-title"><PieChart size={15} /> Mix SaaS vs Hardware</h3>
                    <p className="an-radar-desc">Evolución del revenue recurrente sobre el total mensual.</p>
                    <ResponsiveContainer width="100%" height={210}>
                      <BarChart data={analytics.saasMixTrend} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip
                          formatter={(v, name, props) => [`${fmt(v)} (${name === 'SaaS' ? props?.payload?.saasPct ?? 0 : props?.payload?.hwPct ?? 0}%)`, name]}
                          contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                        />
                        <Legend iconSize={10} />
                        <Bar dataKey="subRev" name="SaaS" fill="#8b5cf6" stackId="a" />
                        <Bar dataKey="hwRev" name="Hardware" fill="#3b82f6" stackId="a" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    {analytics.saasMixTrend.length > 0 && (
                      <div className="an-mix-legend">
                        <span className="an-mix-pct" style={{ color: '#8b5cf6' }}>
                          SaaS actual: {analytics.saasMixTrend[analytics.saasMixTrend.length - 1]?.saasPct ?? 0}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Sección 3: Salud de Clientes ── */}
              <div className="an-section">
                <div className="an-section-header">
                  <span className="an-section-pill"><Heart size={13} /> Salud de Clientes</span>
                  <div className="an-section-stats">
                    {[
                      { label: 'Promedio', value: analytics.avgHealthScore, color: analytics.avgHealthScore >= 60 ? '#10b981' : '#f59e0b' },
                      { label: 'Críticos', value: analytics.healthDistribution.critical, color: '#ef4444' },
                      { label: 'En riesgo', value: analytics.healthDistribution.atRisk, color: '#f59e0b' },
                      { label: 'Saludables', value: analytics.healthDistribution.healthy + analytics.healthDistribution.thriving, color: '#10b981' },
                    ].map(s => (
                      <span key={s.label} className="an-section-stat">
                        <span style={{ color: s.color, fontWeight: 700 }}>{s.value}</span> {s.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="an-grid-health">
                  {/* Health Donut */}
                  <div className="biz-card">
                    <h3 className="biz-card-title"><Heart size={15} /> Distribución</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <RePie>
                        <Pie data={[
                          { name: 'Crítico', value: analytics.healthDistribution.critical },
                          { name: 'En riesgo', value: analytics.healthDistribution.atRisk },
                          { name: 'Sano', value: analytics.healthDistribution.healthy },
                          { name: 'Excelente', value: analytics.healthDistribution.thriving },
                        ].filter(d => d.value > 0)} dataKey="value" nameKey="name"
                          cx="50%" cy="50%" innerRadius={38} outerRadius={75} paddingAngle={3}
                          label={({ name, value }) => `${name}: ${value}`}>
                          {[
                            { v: analytics.healthDistribution.critical, c: '#ef4444' },
                            { v: analytics.healthDistribution.atRisk, c: '#f59e0b' },
                            { v: analytics.healthDistribution.healthy, c: '#3b82f6' },
                            { v: analytics.healthDistribution.thriving, c: '#10b981' },
                          ].filter(d => d.v > 0).map((d, i) => <Cell key={i} fill={d.c} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      </RePie>
                    </ResponsiveContainer>
                  </div>

                  {/* Health Table */}
                  <div className="biz-card an-health-table-card">
                    <h3 className="biz-card-title"><Heart size={15} /> Score por Cliente</h3>
                    <div className="biz-sales-table-wrap" style={{ maxHeight: '260px', overflow: 'auto' }}>
                      <table className="biz-sales-table">
                        <thead><tr><th>Cliente</th><th>Plan</th><th>Dev</th><th>Online</th><th>Score</th><th>Estado</th></tr></thead>
                        <tbody>
                          {analytics.clientsWithHealth.slice(0, 20).map((c, i) => (
                            <tr key={i}>
                              <td>
                                <div className="biz-list-name">{c.name || c.email || '—'}</div>
                                <div className="biz-list-sub" style={{ fontSize: '0.7rem' }}>{c.email}</div>
                              </td>
                              <td><span style={{ color: PLANS[c.plan]?.color, fontSize: '0.78rem' }}>{PLANS[c.plan]?.icon} {PLANS[c.plan]?.name || c.plan}</span></td>
                              <td>{c.deviceCount || 0}</td>
                              <td>{c.activeDeviceCount || 0}</td>
                              <td>
                                <div className="an-health-bar-wrap">
                                  <div className="an-health-bar" style={{ width: `${c.healthScore}%`, background: c.healthScore >= 80 ? '#10b981' : c.healthScore >= 60 ? '#3b82f6' : c.healthScore >= 30 ? '#f59e0b' : '#ef4444' }} />
                                  <span>{c.healthScore}</span>
                                </div>
                              </td>
                              <td>
                                <span className={`an-health-badge ${c.healthScore >= 80 ? 'thriving' : c.healthScore >= 60 ? 'healthy' : c.healthScore >= 30 ? 'risk' : 'critical'}`}>
                                  {c.healthScore >= 80 ? 'Excelente' : c.healthScore >= 60 ? 'Bueno' : c.healthScore >= 30 ? 'En riesgo' : 'Crítico'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Churn Analysis (API) ── */}
              {churnLoading ? (
                <div className="fin-loading"><RefreshCw size={20} className="spin" /> Analizando riesgo de churn...</div>
              ) : churnData && (() => {
                const { clients: churnClients, distribution } = churnData
                const riskColors = { high: '#ef4444', medium: '#f59e0b', low: '#4ade80' }
                const riskLabels = { high: 'Alto', medium: 'Medio', low: 'Bajo' }
                const piData = Object.entries(distribution).filter(([, v]) => v > 0).map(([k, v]) => ({ name: riskLabels[k], value: v, color: riskColors[k] }))
                return (
                  <div className="fin-analytics-section">
                    <h3 className="biz-card-title" style={{ marginBottom: '0.75rem' }}><UserMinus size={16} /> Análisis de Churn</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                      {[
                        { label: 'Total analizados', value: churnClients.length, color: '#a78bfa' },
                        { label: 'Riesgo Alto', value: distribution.high, color: '#ef4444' },
                        { label: 'Riesgo Medio', value: distribution.medium, color: '#f59e0b' },
                        { label: 'Riesgo Bajo', value: distribution.low, color: '#4ade80' },
                      ].map(s => (
                        <div key={s.label} className="biz-stat-card">
                          <div className="biz-stat-value" style={{ color: s.color }}>{s.value}</div>
                          <div className="biz-stat-label">{s.label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1rem' }}>
                      <div className="biz-card">
                        <h3 className="biz-card-title"><PieChart size={15} /> Distribución de Riesgo</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <RePie>
                            <Pie data={piData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                              {piData.map((d, i) => <Cell key={i} fill={d.color} />)}
                            </Pie>
                            <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                          </RePie>
                        </ResponsiveContainer>
                      </div>
                      <div className="biz-card">
                        <h3 className="biz-card-title"><UserMinus size={15} /> Clientes en Riesgo</h3>
                        <div className="biz-sales-table-wrap" style={{ maxHeight: '300px', overflow: 'auto' }}>
                          <table className="biz-sales-table">
                            <thead><tr><th>Cliente</th><th>Plan</th><th>Devices</th><th>Score</th><th>Nivel</th><th>Motivos</th></tr></thead>
                            <tbody>
                              {churnClients.slice(0, 20).map(c => (
                                <tr key={c.userId}>
                                  <td><div className="biz-list-name">{c.name}</div><div className="biz-list-sub">{c.email}</div></td>
                                  <td><span style={{ color: PLANS[c.plan]?.color }}>{PLANS[c.plan]?.icon} {PLANS[c.plan]?.name || c.plan}</span></td>
                                  <td>{c.activeDeviceCount}/{c.deviceCount}</td>
                                  <td style={{ fontWeight: 700, color: riskColors[c.riskLevel] }}>{c.riskScore}</td>
                                  <td><span style={{ padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, background: `${riskColors[c.riskLevel]}20`, color: riskColors[c.riskLevel] }}>{riskLabels[c.riskLevel]}</span></td>
                                  <td style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{c.reasons.join(', ')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* ── Cohort Heatmap (API) ── */}
              {cohortLoading ? (
                <div className="fin-loading"><RefreshCw size={20} className="spin" /> Calculando cohortes...</div>
              ) : cohortData?.cohorts?.length > 0 && (() => {
                const cohorts = cohortData.cohorts
                const maxMonth = Math.max(...cohorts.map(c => Math.max(...Object.keys(c.retention).map(Number))))
                const monthHeaders = Array.from({ length: Math.min(maxMonth + 1, 12) }, (_, i) => i)
                const getColor = pct => {
                  if (pct >= 80) return 'rgba(16, 185, 129, 0.6)'
                  if (pct >= 60) return 'rgba(52, 211, 153, 0.4)'
                  if (pct >= 40) return 'rgba(251, 191, 36, 0.35)'
                  if (pct >= 20) return 'rgba(249, 115, 22, 0.3)'
                  return 'rgba(239, 68, 68, 0.25)'
                }
                const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
                return (
                  <div className="fin-analytics-section">
                    <div className="biz-card">
                      <h3 className="biz-card-title"><Grid3X3 size={15} /> Retención por Cohorte (últimos 12 meses)</h3>
                      <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '1rem' }}>Cada fila es una cohorte mensual. Las celdas muestran % de retención en el mes N después del registro.</p>
                      <div className="biz-sales-table-wrap" style={{ overflow: 'auto' }}>
                        <table className="biz-sales-table" style={{ minWidth: '800px' }}>
                          <thead>
                            <tr>
                              <th>Cohorte</th><th>Registros</th><th>Activos</th>
                              {monthHeaders.map(m => <th key={m} style={{ textAlign: 'center', minWidth: '50px' }}>M{m}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {cohorts.map(c => {
                              const [y, m] = c.month.split('-').map(Number)
                              return (
                                <tr key={c.month}>
                                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{monthNames[m - 1]} {y}</td>
                                  <td>{c.registered}</td>
                                  <td style={{ color: '#4ade80' }}>{c.currentActive}</td>
                                  {monthHeaders.map(mi => {
                                    const pct = c.retention[mi]
                                    return (
                                      <td key={mi} style={{ textAlign: 'center', background: pct != null ? getColor(pct) : 'transparent', fontWeight: 600, fontSize: '0.8rem', color: pct != null ? '#f1f5f9' : '#334155' }}>
                                        {pct != null ? `${pct}%` : '—'}
                                      </td>
                                    )
                                  })}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* ── Revenue Forecast (API) ── */}
              {forecastLoading ? (
                <div className="fin-loading"><RefreshCw size={20} className="spin" /> Calculando forecast...</div>
              ) : forecastData && (() => {
                const chartData = [
                  ...forecastData.history.map(h => ({ month: h.month.slice(5), actual: h.actual, projected: null })),
                  ...forecastData.forecast.map(f => ({ month: f.month.slice(5), actual: f.actual, projected: f.projected })),
                ]
                return (
                  <div className="fin-analytics-section">
                    <h3 className="biz-card-title" style={{ marginBottom: '0.75rem' }}><LineChartIcon size={16} /> Revenue Forecast</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                      {[
                        { label: 'MRR Actual', value: fmt(forecastData.currentMRR), color: '#4ade80' },
                        { label: 'Pipeline Ponderado', value: fmt(forecastData.pipelineWeighted), color: '#a78bfa' },
                        { label: 'Recurrentes', value: fmt(forecastData.recurringMonthly), color: '#3b82f6' },
                        { label: 'Proyectado Mensual', value: fmt(forecastData.currentMRR + forecastData.recurringMonthly), color: '#f59e0b' },
                      ].map(s => (
                        <div key={s.label} className="biz-stat-card">
                          <div className="biz-stat-value" style={{ color: s.color, fontSize: '1.1rem' }}>{s.value}</div>
                          <div className="biz-stat-label">{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {forecastData.planBreakdown && Object.keys(forecastData.planBreakdown).length > 0 && (
                      <div className="biz-card" style={{ marginBottom: '1rem' }}>
                        <h3 className="biz-card-title"><BarChart2 size={15} /> MRR por Plan</h3>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          {Object.entries(forecastData.planBreakdown).map(([plan, data]) => {
                            const amount = typeof data === 'object' ? data.amount : data
                            const count = typeof data === 'object' ? data.count : null
                            return (
                            <div key={plan} style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', background: `${PLANS[plan]?.color || '#6b7280'}15`, border: `1px solid ${PLANS[plan]?.color || '#6b7280'}30` }}>
                              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{PLANS[plan]?.icon} {PLANS[plan]?.name || plan}{count != null ? ` · ${count} cliente${count !== 1 ? 's' : ''}` : ''}</div>
                              <div style={{ fontSize: '1rem', fontWeight: 700, color: PLANS[plan]?.color || '#e2e8f0' }}>{fmt(amount)}</div>
                            </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    <div className="biz-card">
                      <h3 className="biz-card-title"><LineChartIcon size={15} /> Revenue: Historial + Proyección (6 meses)</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                          <YAxis tickFormatter={v => fmt(v)} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} formatter={v => v != null ? fmt(v) : '—'} />
                          <Legend />
                          <Bar dataKey="actual" name="Real" fill="#4ade80" radius={[4, 4, 0, 0]} />
                          <Line dataKey="projected" name="Proyectado" stroke="#f59e0b" strokeDasharray="5 5" strokeWidth={2} dot={{ fill: '#f59e0b', r: 4 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )
              })()}

              {/* ── Cost Allocation (API) ── */}
              {costAllocLoading ? (
                <div className="fin-loading"><RefreshCw size={20} className="spin" /> Calculando costos por cliente...</div>
              ) : costAllocData && (() => {
                const { clients: costClients, totalMonthlyCostUSD, totalDevices: totalDevs, totalActiveDevices } = costAllocData
                return (
                  <div className="fin-analytics-section">
                    <h3 className="biz-card-title" style={{ marginBottom: '0.75rem' }}><Server size={16} /> Costos por Cliente</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                      {[
                        { label: 'Costo Mensual AWS', value: `USD ${totalMonthlyCostUSD}`, color: '#ef4444' },
                        { label: 'Dispositivos Totales', value: totalDevs, color: '#a78bfa' },
                        { label: 'Dispositivos Activos', value: totalActiveDevices, color: '#4ade80' },
                        { label: 'Costo/Device Activo', value: totalActiveDevices > 0 ? `USD ${(totalMonthlyCostUSD / totalActiveDevices).toFixed(2)}` : '—', color: '#f59e0b' },
                      ].map(s => (
                        <div key={s.label} className="biz-stat-card">
                          <div className="biz-stat-value" style={{ color: s.color }}>{s.value}</div>
                          <div className="biz-stat-label">{s.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="biz-card" style={{ marginBottom: '1rem' }}>
                      <h3 className="biz-card-title"><BarChart2 size={15} /> Top 10 — Costo Estimado por Cliente</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={costClients.slice(0, 10)} layout="vertical" margin={{ left: 100, right: 20, top: 10, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `$${v}`} />
                          <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={90} />
                          <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} formatter={v => `USD ${v}`} />
                          <Bar dataKey="estimatedCostUSD" name="Costo USD" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="biz-card">
                      <h3 className="biz-card-title"><Server size={15} /> Detalle de Costos</h3>
                      <div className="biz-sales-table-wrap" style={{ maxHeight: '350px', overflow: 'auto' }}>
                        <table className="biz-sales-table">
                          <thead><tr><th>Cliente</th><th>Plan</th><th>Devices</th><th>Activos</th><th>Costo USD</th><th>Revenue ARS</th><th>Margen</th></tr></thead>
                          <tbody>
                            {costClients.map(c => (
                              <tr key={c.userId}>
                                <td><div className="biz-list-name">{c.name}</div><div className="biz-list-sub">{c.email}</div></td>
                                <td><span style={{ color: PLANS[c.plan]?.color }}>{PLANS[c.plan]?.icon} {PLANS[c.plan]?.name || c.plan}</span></td>
                                <td>{c.deviceCount}</td>
                                <td>{c.activeDeviceCount}</td>
                                <td style={{ fontWeight: 600, color: '#ef4444' }}>USD {c.estimatedCostUSD}</td>
                                <td>{c.revenueARS > 0 ? fmt(c.revenueARS) : <span style={{ color: '#64748b' }}>—</span>}</td>
                                <td style={{ fontWeight: 600, color: c.marginPct >= 0 ? '#4ade80' : '#ef4444' }}>{c.marginPct}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* ── Flujo de Caja ── */}
          {tab === 'flujo-caja' && (
            <div className="biz-page">
              <div className="cfa-section">
                <h2 className="cfa-title"><DollarSign size={20} /> Asignación del Flujo de Caja</h2>
                <p className="cfa-desc">Distribución mensual de ingresos: cuánto va a costos, impuestos, y cuánto queda para repartir. Todos los valores son ajustables para simular escenarios.</p>

                {cashAllocation.isSimulating && (
                  <div className="cfa-sim-banner">
                    <AlertTriangle size={14} /> Modo simulación activo — valores modificados manualmente
                    <button className="cfa-sim-reset" onClick={() => { setSimDevRev(null); setSimMRR(null); setSimRepos(null) }}>Resetear</button>
                  </div>
                )}

                {/* Waterfall: INGRESO → -REPOS → -IMPUESTOS → =GANANCIA → COMISIÓN / REINVERSIÓN → =NETA */}
                <div className="cfa-waterfall">
                  {/* 1. INGRESO TOTAL */}
                  <div className="cfa-wf-step income">
                    <div className="cfa-wf-label">Ingreso Total</div>
                    <div className="cfa-wf-value">{fmt(cashAllocation.totalIncome)}</div>
                    <div className="cfa-wf-sub">Equipos {fmt(cashAllocation.effectiveDevRev)} + MRR {fmt(cashAllocation.effectiveMRR)}</div>
                    <div className="cfa-sim-split">
                      <label>HW<input type="number" className="cfa-sim-input" value={simDevRev !== null ? simDevRev : cashAllocation.curDevRev} onChange={e => { const v = Number(e.target.value); setSimDevRev(v === cashAllocation.curDevRev ? null : v) }} step={10000} /></label>
                      <label>MRR<input type="number" className="cfa-sim-input" value={simMRR !== null ? simMRR : cashAllocation.curMRR} onChange={e => { const v = Number(e.target.value); setSimMRR(v === cashAllocation.curMRR ? null : v) }} step={5000} /></label>
                    </div>
                  </div>
                  <div className="cfa-wf-arrow">→</div>

                  {/* 2. - REPOS. INVENTARIO */}
                  <div className="cfa-wf-step inventory">
                    <div className="cfa-wf-label">− Repos. Inv.</div>
                    <div className="cfa-wf-value">−{fmt(cashAllocation.inventoryReserve)}</div>
                    <div className="cfa-wf-sub">Stock + crecimiento</div>
                    <input type="number" className="cfa-sim-input" value={simRepos !== null ? simRepos : cashAllocation.realRepos} onChange={e => { const v = Number(e.target.value); setSimRepos(v === cashAllocation.realRepos ? null : v) }} step={5000} />
                  </div>
                  <div className="cfa-wf-arrow">→</div>

                  {/* 4. - IMPUESTOS */}
                  <div className="cfa-wf-step tax">
                    <div className="cfa-wf-label">− Impuestos</div>
                    <div className="cfa-wf-value">−{fmt(cashAllocation.tax)}</div>
                    <div className="cfa-wf-slider">
                      <input type="range" min={0} max={55} value={taxRate} onChange={e => setTaxRate(Number(e.target.value))} />
                      <span>{taxRate}%</span>
                    </div>
                  </div>

                  {/* Gastos Op. (si > 0) */}
                  {monthlyOpex > 0 && <>
                    <div className="cfa-wf-arrow">→</div>
                    <div className="cfa-wf-step opex">
                      <div className="cfa-wf-label">− Gastos Op.</div>
                      <div className="cfa-wf-value">−{fmt(cashAllocation.opex)}</div>
                    </div>
                  </>}

                  <div className="cfa-wf-arrow">→</div>

                  {/* 5. = GANANCIA (paso intermedio) */}
                  <div className="cfa-wf-step ganancia">
                    <div className="cfa-wf-label">= Ganancia</div>
                    <div className="cfa-wf-value">{fmt(cashAllocation.ganancia)}</div>
                    <div className="cfa-wf-sub">Disponible para repartir</div>
                  </div>
                  <div className="cfa-wf-arrow">→</div>

                  {/* 6a. COMISIONES (% del precio de venta HW) */}
                  <div className="cfa-wf-step commission">
                    <div className="cfa-wf-label">→ Comisiones</div>
                    <div className="cfa-wf-value">−{fmt(cashAllocation.commission)}</div>
                    <div className="cfa-wf-sub" style={{ fontSize: '11px', color: '#94a3b8' }}>
                      3D {armado3dPct}% · Elec {armadoElecPct}% · Venta {ventaPct}%
                    </div>
                    <div className="cfa-wf-slider" style={{ marginTop: 6 }}>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>3D</span>
                      <input type="range" min={0} max={20} value={armado3dPct} onChange={e => setArmado3dPct(Number(e.target.value))} />
                      <span>{armado3dPct}%</span>
                    </div>
                    <div className="cfa-wf-slider">
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>Elec</span>
                      <input type="range" min={0} max={20} value={armadoElecPct} onChange={e => setArmadoElecPct(Number(e.target.value))} />
                      <span>{armadoElecPct}%</span>
                    </div>
                    <div className="cfa-wf-slider">
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>Venta</span>
                      <input type="range" min={0} max={30} value={ventaPct} onChange={e => setVentaPct(Number(e.target.value))} />
                      <span>{ventaPct}%</span>
                    </div>
                  </div>
                  <div className="cfa-wf-arrow">→</div>

                  {/* 6b. REINVERSIÓN (% de ganancia) */}
                  <div className="cfa-wf-step reinvest">
                    <div className="cfa-wf-label">→ Reinversión</div>
                    <div className="cfa-wf-value">−{fmt(cashAllocation.reinvestment)}</div>
                    <div className="cfa-wf-slider">
                      <input type="range" min={0} max={60} value={reinvestPct} onChange={e => setReinvestPct(Number(e.target.value))} />
                      <span>{reinvestPct}%</span>
                    </div>
                  </div>
                  <div className="cfa-wf-arrow">→</div>

                  {/* 7. = GANANCIA NETA */}
                  <div className="cfa-wf-step profit">
                    <div className="cfa-wf-label">= Ganancia Neta</div>
                    <div className="cfa-wf-value">{fmt(cashAllocation.freeCash)}</div>
                    <div className="cfa-wf-sub">Margen neto: {cashAllocation.netMarginPct}%</div>
                  </div>
                </div>

                {/* Gastos operativos input */}
                <div className="cfa-config" style={{ marginTop: '12px' }}>
                  <div className="cfa-config-item">
                    <label><CreditCard size={14} /> Gastos operativos /mes</label>
                    <input type="number" className="cfa-opex-input" min={0} step={1000} value={monthlyOpex} onChange={e => setMonthlyOpex(Number(e.target.value) || 0)} placeholder="0" />
                  </div>
                </div>

                {/* Sankey: Flujo de Distribución */}
                <div className="biz-card" style={{ marginTop: '16px' }}>
                  <h3 className="biz-card-title"><TrendingUp size={15} /> Flujo de Distribución</h3>
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 12px' }}>Cómo fluye cada peso desde las ventas hasta cada participante</p>
                  <CashFlowSankey cashAllocation={cashAllocation} monthlySnapshots={cashAllocation.monthlySnapshots} armado3dPct={armado3dPct} armadoElecPct={armadoElecPct} ventaPct={ventaPct} reinvestPct={reinvestPct} taxRate={taxRate} />
                </div>

                {/* Charts row: stacked bar trend + pie */}
                <div className="cfa-charts-row">
                  <div className="biz-card">
                    <h3 className="biz-card-title"><BarChart2 size={15} /> Tendencia Mensual (6 meses)</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={cashAllocation.monthlyTrend} stackOffset="sign">
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                        <Legend />
                        <Bar dataKey="comision" stackId="a" fill="#ec4899" name="Comisiones" />
                        <Bar dataKey="impuestos" stackId="a" fill="#ef4444" name="Impuestos" />
                        <Bar dataKey="inventario" stackId="a" fill="#f59e0b" name="Repos. Inventario" />
                        <Bar dataKey="reinversion" stackId="a" fill="#8b5cf6" name="Reinversión" />
                        <Bar dataKey="ganancia" stackId="a" fill="#10b981" name="Ganancia Neta" radius={[4, 4, 0, 0]} />
                        <Line type="monotone" dataKey="total" stroke="#e2e8f0" strokeWidth={2} strokeDasharray="6 3" dot={{ fill: '#e2e8f0', r: 3 }} name="Ingreso Total" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="biz-card">
                    <h3 className="biz-card-title"><PieChart size={15} /> Distribución Mes Actual</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <RePie>
                        <Pie data={cashAllocation.allocationPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                          innerRadius={45} outerRadius={85} paddingAngle={3}
                          label={({ name, value }) => `${name}: ${fmt(value)}`}>
                          {cashAllocation.allocationPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      </RePie>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Recommendations */}
                {cashAllocation.recs.length > 0 && (
                  <div className="cfa-recs">
                    <h3 className="biz-card-title"><Zap size={15} /> Recomendaciones</h3>
                    {cashAllocation.recs.map((r, i) => (
                      <div key={i} className={`cfa-rec ${r.type}`}>
                        {r.type === 'danger' && <AlertTriangle size={16} />}
                        {r.type === 'warning' && <AlertTriangle size={16} />}
                        {r.type === 'growth' && <TrendingUp size={16} />}
                        {r.type === 'info' && <Target size={16} />}
                        <span>{r.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── MercadoPago ── */}
          {tab === 'mercadopago' && (
            <div className="mp-page">
              {mpError && (
                <div className="mp-error">
                  <AlertTriangle size={18} />
                  <span>Error al cargar datos de MercadoPago: {mpError}</span>
                  <button className="fin-btn primary" onClick={loadMPData}><RefreshCw size={14} /> Reintentar</button>
                </div>
              )}

              {mpLoading && !mpAccount ? (
                <div className="fin-loading"><RefreshCw size={20} className="spin" /> Cargando datos de MercadoPago...</div>
              ) : mpAccount && (
                <>
                  {/* Account + Balance */}
                  <div className="mp-account-header">
                    <div className="mp-account-info">
                      <Wallet size={22} />
                      <div>
                        <h3>{mpAccount.nickname || mpAccount.email}</h3>
                        <span className="mp-account-email">{mpAccount.email}</span>
                        <span className={`mp-status-badge ${mpAccount.status === 'active' ? 'active' : ''}`}>
                          {mpAccount.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <button className="fin-btn primary" onClick={loadMPData} disabled={mpLoading}>
                      <RefreshCw size={14} className={mpLoading ? 'spin' : ''} /> Actualizar
                    </button>
                  </div>

                  {/* Balance KPIs */}
                  {(mpBalance?.balance || mpAccount.balance) && (() => {
                    const bal = mpBalance?.balance || mpAccount.balance
                    return (
                      <div className="fin-kpis">
                        <div className="fin-kpi accent">
                          <DollarSign size={20} />
                          <div>
                            <span className="fin-kpi-value">{fmt(bal.availableBalance)}</span>
                            <span className="fin-kpi-label">Disponible</span>
                          </div>
                        </div>
                        <div className="fin-kpi">
                          <Clock size={20} />
                          <div>
                            <span className="fin-kpi-value">{fmt(bal.unavailableBalance)}</span>
                            <span className="fin-kpi-label">No disponible</span>
                          </div>
                        </div>
                        <div className="fin-kpi">
                          <Wallet size={20} />
                          <div>
                            <span className="fin-kpi-value">{fmt(bal.totalAmount)}</span>
                            <span className="fin-kpi-label">Total en cuenta</span>
                          </div>
                        </div>
                        {mpBalance?.reserves && (
                          <div className="fin-kpi">
                            <Shield size={20} />
                            <div>
                              <span className="fin-kpi-value">{fmt(mpBalance.reserves.totalPendingRelease)}</span>
                              <span className="fin-kpi-label">Por liberar</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Upcoming Releases */}
                  {mpBalance?.reserves?.upcomingReleases?.length > 0 && (
                    <div className="mp-releases-section">
                      <h4><CalendarDays size={16} /> Próximas liberaciones</h4>
                      <div className="mp-releases-list">
                        {mpBalance.reserves.upcomingReleases.map(r => (
                          <div key={r.date} className="mp-release-item">
                            <span className="mp-release-date">{new Date(r.date + 'T00:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}</span>
                            <span className="mp-release-amount">{fmt(r.amount)}</span>
                            <span className="mp-release-count">{r.count} pago{r.count !== 1 ? 's' : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subscriptions Section */}
                  {mpSubscriptions && (() => {
                    const allSubs = mpSubscriptions.subscriptions
                    const activeSubs = allSubs.filter(s => s.status === 'authorized')
                    const pendingSubs = allSubs.filter(s => s.status === 'pending')
                    const getMonthlyAmount = (sub) => {
                      const amount = sub.autoRecurring?.transactionAmount || 0
                      const freq = sub.autoRecurring?.frequency || 1
                      return freq > 1 ? Math.round(amount / freq) : amount
                    }
                    const subMrr = activeSubs.reduce((sum, s) => sum + getMonthlyAmount(s), 0)

                    // Sort: active first, then pending, paused, cancelled
                    const statusOrder = { authorized: 0, pending: 1, paused: 2, cancelled: 3 }
                    const sortedSubs = [...allSubs].sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9))

                    const formatPlan = (reason) => {
                      if (!reason) return '—'
                      return reason.replace(/^GrowPro\s+/i, '').replace(/\s*-\s*Mensual$/i, '').replace(/^Plan\s+/i, '')
                    }

                    const getPlanClass = (reason) => {
                      if (!reason) return ''
                      const r = reason.toLowerCase()
                      if (r.includes('comercial')) return 'plan-comercial'
                      if (r.includes('básico') || r.includes('basico') || r.includes('growpro básico')) return 'plan-basico'
                      if (r.includes('pro+') || r.includes('proplus') || r.includes('pro')) return 'plan-pro'
                      return ''
                    }

                    const getAge = (dateStr) => {
                      const days = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24))
                      if (days < 1) return 'Hoy'
                      if (days < 30) return `${days}d`
                      const months = Math.floor(days / 30)
                      if (months < 12) return `${months}m`
                      const years = Math.floor(months / 12)
                      const rem = months % 12
                      return rem > 0 ? `${years}a ${rem}m` : `${years}a`
                    }

                    return (
                    <>
                      {/* Subscription KPIs */}
                      <div className="fin-kpis">
                        <div className="fin-kpi accent">
                          <CreditCard size={20} />
                          <div>
                            <span className="fin-kpi-value">{fmt(subMrr)}</span>
                            <span className="fin-kpi-label">MRR Suscripciones</span>
                          </div>
                        </div>
                        <div className="fin-kpi">
                          <CheckCircle size={20} />
                          <div>
                            <span className="fin-kpi-value">{activeSubs.length}</span>
                            <span className="fin-kpi-label">Activas</span>
                          </div>
                        </div>
                        <div className="fin-kpi warning">
                          <Clock size={20} />
                          <div>
                            <span className="fin-kpi-value">{pendingSubs.length}</span>
                            <span className="fin-kpi-label">Pendientes</span>
                          </div>
                        </div>
                        <div className="fin-kpi danger">
                          <XCircle size={20} />
                          <div>
                            <span className="fin-kpi-value">{mpSubscriptions.summary.cancelled}</span>
                            <span className="fin-kpi-label">Canceladas</span>
                          </div>
                        </div>
                        <div className="fin-kpi">
                          <Users size={20} />
                          <div>
                            <span className="fin-kpi-value">{mpSubscriptions.summary.total}</span>
                            <span className="fin-kpi-label">Total (90d)</span>
                          </div>
                        </div>
                      </div>

                      <div className="mp-section">
                        <div className="mp-section-header">
                          <h3><CreditCard size={18} /> Suscripciones en MercadoPago</h3>
                          <div className="mp-filters">
                            {['', 'authorized', 'paused', 'cancelled', 'pending'].map(f => {
                              const labels = { '': 'Todas', authorized: 'Activas', paused: 'Pausadas', cancelled: 'Canceladas', pending: 'Pendientes' }
                              const counts = { '': mpSubscriptions.summary.total, authorized: mpSubscriptions.summary.authorized, paused: mpSubscriptions.summary.paused, cancelled: mpSubscriptions.summary.cancelled, pending: mpSubscriptions.summary.pending }
                              return (
                                <button key={f} className={`mp-filter-btn ${mpSubFilter === f ? 'active' : ''}`}
                                  onClick={() => setMpSubFilter(f)}>
                                  {labels[f]} ({counts[f]})
                                </button>
                              )
                            })}
                          </div>
                        </div>

                        <div className="mp-table-wrap">
                          <table className="biz-sales-table">
                            <thead>
                              <tr>
                                <th>Cliente</th>
                                <th>Plan</th>
                                <th>Estado</th>
                                <th>Monto/mes</th>
                                <th>Próximo cobro</th>
                                <th>Antigüedad</th>
                                <th>Creada</th>
                                <th>Fecha baja</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedSubs.map(sub => (
                                <tr key={sub.id} className={sub.status === 'cancelled' ? 'mp-row-cancelled' : ''}>
                                  <td className="mp-sub-client">
                                    {sub.resolvedName && <span className="mp-sub-name">{sub.resolvedName}</span>}
                                    <span className="mp-sub-email">{sub.resolvedEmail || sub.payerEmail || '—'}</span>
                                  </td>
                                  <td><span className={`mp-plan-badge ${getPlanClass(sub.reason)}`}>{formatPlan(sub.reason)}</span></td>
                                  <td>
                                    <span className={`mp-status-pill ${sub.status}`}>
                                      {sub.status === 'authorized' ? 'Activa' : sub.status === 'paused' ? 'Pausada' : sub.status === 'cancelled' ? 'Cancelada' : sub.status}
                                    </span>
                                  </td>
                                  <td className="mp-sub-amount">{sub.autoRecurring ? fmt(getMonthlyAmount(sub)) : '—'}</td>
                                  <td>{(() => {
                                    if (sub.nextPaymentDate) return new Date(sub.nextPaymentDate).toLocaleDateString('es-AR')
                                    if (sub.status === 'authorized' || sub.status === 'pending') {
                                      const now = new Date()
                                      const thisMonth10 = new Date(now.getFullYear(), now.getMonth(), 10)
                                      const next = thisMonth10 > now ? thisMonth10 : new Date(now.getFullYear(), now.getMonth() + 1, 10)
                                      return next.toLocaleDateString('es-AR')
                                    }
                                    return '—'
                                  })()}</td>
                                  <td className="mp-sub-age">{getAge(sub.dateCreated)}</td>
                                  <td>{new Date(sub.dateCreated).toLocaleDateString('es-AR')}</td>
                                  <td className="mp-sub-cancelled">{(sub.status === 'cancelled' || sub.status === 'paused') && sub.lastModified ? new Date(sub.lastModified).toLocaleDateString('es-AR') : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                    )
                  })()}
                </>
              )}
            </div>
          )}

        </>
      )}
    </div>
  )
}
